import React from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';

import { CurrencyCode } from '@ndvr/portfolio-api';

import { formatCurrency, formatCurrencyHuman } from 'utils';

import { AssetClassExposureSlice } from '../assetClassExposure';
import { getCategoryColor } from '../constants';

interface AssetClassExposureChartProps {
    slices: AssetClassExposureSlice[];
    totalExposure: number;
    isError?: boolean;
}

export const AssetClassExposureChart = ({ slices, totalExposure, isError = false }: AssetClassExposureChartProps) => {
    if (isError) {
        return <div className="flex h-48 items-center justify-center text-sm text-gray-400">Exposure unavailable</div>;
    }

    if (slices.length === 0) {
        return <div className="flex h-48 items-center justify-center text-sm text-gray-400">No exposure data</div>;
    }

    const totalExposureSum = slices.reduce((sum, s) => sum + s.exposure, 0);
    const chartData = slices.map((slice) => ({
        ...slice,
        color: getCategoryColor(slice.category),
        pct: totalExposureSum > 0 ? ((slice.exposure / totalExposureSum) * 100).toFixed(1) : '0.0',
    }));

    return (
        <div className="flex w-full flex-col items-center">
            <div className="relative h-[160px] w-full">
                <PieChart
                    responsive
                    style={{ width: '100%', height: '100%' }}
                    className="[&_*]:!outline-none [&_.recharts-pie-sector]:!cursor-default [&_.recharts-sector]:!outline-none"
                >
                    <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        paddingAngle={0}
                        dataKey="exposure"
                        nameKey="label"
                        stroke="#fff"
                        strokeWidth={2}
                    >
                        {chartData.map((entry) => (
                            <Cell key={entry.category} fill={entry.color} />
                        ))}
                    </Pie>
                    <Tooltip
                        formatter={(value, name) => [
                            formatCurrency(Array.isArray(value) ? value[0] : value),
                            name ?? '',
                        ]}
                        itemStyle={{ color: 'var(--color-gray-800)' }}
                        contentStyle={{ fontSize: '12px', borderRadius: '6px', zIndex: 10 }}
                        wrapperStyle={{ zIndex: 10 }}
                    />
                </PieChart>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-xs text-gray-500">Total</div>
                        <div className="text-xs font-bold">{formatCurrencyHuman(totalExposure, CurrencyCode.USD)}</div>
                    </div>
                </div>
            </div>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 pt-1">
                {chartData.map((entry) => (
                    <div key={entry.category} className="flex items-center gap-1">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: entry.color }} />
                        <span className="text-xs text-gray-600">{entry.label}</span>
                        <span className="text-xs font-medium text-gray-800">{entry.pct}%</span>
                    </div>
                ))}
            </div>
        </div>
    );
};
